import dayjs from "dayjs";
import { parseServerUtc } from "./lotClock";

/**
 * Why Sell is off once resale of an auction has closed (#9 / backend#214), with the close in the
 * player's own timezone. The server's refusal says the same with the UTC time.
 *
 * @param {string} closesAt `resaleClosesAt` of a Sell Auctions row (UTC, with or without a "Z").
 * @returns {string|null} null when there is no date, so the caller keeps the server's message.
 */
export const resaleClosedMessage = (closesAt) => {
  const ms = parseServerUtc(closesAt);
  if (!Number.isFinite(ms)) return null;
  return `Resale for this contest closed on ${dayjs(ms).format("MM/DD/YYYY hh:mm A")}, 1 day before its first match.`;
};

/**
 * SMBidSell refusals (errorCode) after which the auction takes no listing again, so the Sell list is
 * reloaded: resale has closed (#9), or the contest has started (backend#214).
 */
export const RESALE_OVER_CODES = ["RESALE_CLOSED", "CONTEST_STARTED"];

/**
 * What a refused listing says (backend#214). RESALE_CLOSED is worded here, with the close in the
 * player's timezone (the server's text has UTC); every other refusal, CONTEST_STARTED included, keeps
 * the server's text, which has no time to convert.
 *
 * @param {string} errorCode The refusal's `errorCode`, if it has one.
 * @param {string} closesAt `resaleClosesAt` of the row being sold.
 * @param {string} serverMessage The server's message.
 * @returns {string}
 */
export const sellRefusalMessage = (errorCode, closesAt, serverMessage) =>
  (errorCode === "RESALE_CLOSED" && resaleClosedMessage(closesAt)) || serverMessage;

/**
 * Checks the Resale Bid (Sell) dialog before a listing is requested (#136, WEB-P4-114). Every field
 * is checked at once so all of its messages show together.
 *
 * @param {{startingBid: *, ownerShipSold: *, startDate: *}} form The dialog's values, as typed.
 * @returns {{dateOk: boolean, shareOk: boolean, bidOk: boolean, startingBid: number}} startingBid
 *   is the Minimum Bid as a number (NaN when it is not one).
 */
export const checkSellForm = (form) => {
  const typed = String(form?.startingBid ?? "").trim();
  const startingBid = typed === "" ? NaN : Number(typed);
  return {
    dateOk: Boolean(form?.startDate),
    shareOk: Number(form?.ownerShipSold) > 0,
    bidOk: Number.isFinite(startingBid) && startingBid > 0,
    startingBid,
  };
};
